import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, Copy, Download, Eye, Settings, Palette, 
  Type, Layers, Sparkles, CheckCircle, ChevronDown, ArrowLeft,
  // Import all the icons we want to customize
  Home, User, Settings as SettingsIcon, Mail, Phone, Calendar,
  Star, Heart, Bookmark, Share, Download as DownloadIcon,
  Edit, Trash2, Plus, Minus, X, Check, ArrowRight, ArrowLeft as ArrowLeftIcon,
  ChevronUp, ChevronRight, ChevronDown as ChevronDownIcon,
  Menu, MoreHorizontal, MoreVertical, Filter, ArrowUpDown
} from 'lucide-react';
import { PlateStack } from '../components/PlateStack';
import { StairsStack } from '../components/StairsStack';

type IconComponent = React.ComponentType<any>;
type IconVariant = 'outline' | 'filled' | 'gradient' | 'custom';

interface IconDefinition {
  name: string;
  component: IconComponent;
  category: string;
  description: string;
  variants: IconVariant[];
  customProps?: Record<string, any>;
}

interface CustomizerState {
  selectedIcon: IconDefinition | null;
  selectedVariant: IconVariant;
  size: number;
  customSize: string; // for manual size input
  color: string;
  strokeWidth: number;
  fillOpacity: number;
  stepOpacities: number[]; // individual step opacities for StairsStack
  stepColors: string[]; // individual step colors for StairsStack
  customProps: Record<string, any>;
}

const iconDefinitions: IconDefinition[] = [
  // Basic Icons
  {
    name: 'Home',
    component: Home,
    category: 'Navigation',
    description: 'Home or main page navigation',
    variants: ['outline', 'filled']
  },
  {
    name: 'User',
    component: User,
    category: 'User',
    description: 'User profile or account',
    variants: ['outline', 'filled']
  },
  {
    name: 'Settings',
    component: SettingsIcon,
    category: 'System',
    description: 'Settings or configuration',
    variants: ['outline', 'filled']
  },
  {
    name: 'Mail',
    component: Mail,
    category: 'Communication',
    description: 'Email or messaging',
    variants: ['outline', 'filled']
  },
  {
    name: 'Phone',
    component: Phone,
    category: 'Communication',
    description: 'Phone or contact',
    variants: ['outline', 'filled']
  },
  {
    name: 'Calendar',
    component: Calendar,
    category: 'Time',
    description: 'Calendar or scheduling',
    variants: ['outline', 'filled']
  },
  // Actions
  {
    name: 'Star',
    component: Star,
    category: 'Actions',
    description: 'Favorite or rating',
    variants: ['outline', 'filled']
  },
  {
    name: 'Heart',
    component: Heart,
    category: 'Actions',
    description: 'Like or love',
    variants: ['outline', 'filled']
  },
  {
    name: 'Bookmark',
    component: Bookmark,
    category: 'Actions',
    description: 'Save or bookmark',
    variants: ['outline', 'filled']
  },
  {
    name: 'Share',
    component: Share,
    category: 'Actions',
    description: 'Share content',
    variants: ['outline', 'filled']
  },
  {
    name: 'Download',
    component: DownloadIcon,
    category: 'Actions',
    description: 'Download file',
    variants: ['outline', 'filled']
  },
  // Editing
  {
    name: 'Edit',
    component: Edit,
    category: 'Editing',
    description: 'Edit or modify',
    variants: ['outline', 'filled']
  },
  {
    name: 'Trash',
    component: Trash2,
    category: 'Editing',
    description: 'Delete or remove',
    variants: ['outline', 'filled']
  },
  {
    name: 'Plus',
    component: Plus,
    category: 'Editing',
    description: 'Add or create',
    variants: ['outline', 'filled']
  },
  {
    name: 'Minus',
    component: Minus,
    category: 'Editing',
    description: 'Remove or subtract',
    variants: ['outline', 'filled']
  },
  {
    name: 'X',
    component: X,
    category: 'Editing',
    description: 'Close or cancel',
    variants: ['outline', 'filled']
  },
  {
    name: 'Check',
    component: Check,
    category: 'Editing',
    description: 'Confirm or complete',
    variants: ['outline', 'filled']
  },
  // Arrows
  {
    name: 'ArrowRight',
    component: ArrowRight,
    category: 'Arrows',
    description: 'Right direction or next',
    variants: ['outline', 'filled']
  },
  {
    name: 'ArrowLeft',
    component: ArrowLeft,
    category: 'Arrows',
    description: 'Left direction or previous',
    variants: ['outline', 'filled']
  },
  {
    name: 'ChevronUp',
    component: ChevronUp,
    category: 'Arrows',
    description: 'Up direction or expand',
    variants: ['outline', 'filled']
  },
  {
    name: 'ChevronRight',
    component: ChevronRight,
    category: 'Arrows',
    description: 'Right direction or next',
    variants: ['outline', 'filled']
  },
  {
    name: 'ChevronDown',
    component: ChevronDownIcon,
    category: 'Arrows',
    description: 'Down direction or collapse',
    variants: ['outline', 'filled']
  },
  // Interface
  {
    name: 'Menu',
    component: Menu,
    category: 'Interface',
    description: 'Menu or navigation',
    variants: ['outline', 'filled']
  },
  {
    name: 'MoreHorizontal',
    component: MoreHorizontal,
    category: 'Interface',
    description: 'More options (horizontal)',
    variants: ['outline', 'filled']
  },
  {
    name: 'MoreVertical',
    component: MoreVertical,
    category: 'Interface',
    description: 'More options (vertical)',
    variants: ['outline', 'filled']
  },
  {
    name: 'Filter',
    component: Filter,
    category: 'Interface',
    description: 'Filter or sort',
    variants: ['outline', 'filled']
  },
  {
    name: 'ArrowUpDown',
    component: ArrowUpDown,
    category: 'Interface',
    description: 'Sort or organize',
    variants: ['outline', 'filled']
  },
  // Lab Icons
  {
    name: 'Stairs',
    component: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="10" height="4" x="2" y="16"/>
        <rect width="10" height="4" x="4" y="12"/>
        <rect width="10" height="4" x="6" y="8"/>
        <rect width="10" height="4" x="8" y="4"/>
        <path d="M12 20h10V4h-4"/>
      </svg>
    ),
    category: 'Lab',
    description: 'Stairs or steps icon from Lucide Lab',
    variants: ['outline', 'filled']
  },
  {
    name: 'Layers',
    component: Layers,
    category: 'Lab',
    description: 'Layered elements icon',
    variants: ['outline', 'filled']
  },
  // Custom Components
  {
    name: 'StairsStack',
    component: StairsStack,
    category: 'Custom',
    description: 'Layered stairs visualization',
    variants: ['custom'],
    customProps: {
      variant: ['outline', 'filled', 'gradient'],
      size: ['sm', 'md', 'lg', 'xl'],
      count: [3, 4, 5, 6, 7],
      showConnector: [true, false]
    }
  }
];

const colorRamps = {
  'Slate': {
    // Primary brand color — Anchor at 70%
    base: '#5A7D5E',
    tints: ['#FFFFFF', '#F2F7F3', '#D9E8DC', '#B5D1BA', '#84B18C', '#6A9C73'],
    shades: ['#465F49', '#354738', '#1E2A20']
  },
  'Alpine': {
    // Secondary brand color — Anchor at 80%
    base: '#53675B',
    tints: ['#FFFFFF', '#E8F4EE', '#C6E1D2', '#A3CCB5', '#73AA8C', '#5E9876'],
    shades: ['#497962', '#34423B', '#19241C']
  },
  'Charcoal': {
    // Neutral color — Anchor at 60%
    base: '#9CA3AF',
    tints: ['#FFFFFF', '#F6F8FA', '#ECEFF3', '#DFE3EA', '#B7BCC6'],
    shades: ['#808894', '#555D6A', '#393C43', '#1C1E22']
  },
  'Mustard': {
    // Accent/highlight — Anchor at 75%
    base: '#E3B505',
    tints: ['#FFFFFF', '#FEF8DF', '#FCEEB5', '#FAE270', '#F4CF12', '#EFC708', '#E8C00A'],
    shades: ['#B89003', '#9C7B02']
  },
  'Coral': {
    // Secondary accent/highlight — Anchor at 85%
    base: '#F85E5E',
    tints: ['#FFFFFF', '#FEEBEB', '#FDD6D6', '#FBB8B8', '#F78383', '#F26767', '#F14646', '#F73737'],
    shades: ['#960D0D']
  },
  'Brown': {
    // Earthy accent — Anchor at 70%
    base: '#584E47',
    tints: ['#FFFFFF', '#F6F2F0', '#E7DFDB', '#D6C6B8', '#B9A28B', '#A28970'],
    shades: ['#302A26', '#211C18', '#120F0C']
  },
  // Experimental Askew Complements
  'Ocean': {
    // Deep Ocean Teal — Anchor at 65%
    base: '#2F5C5B',
    tints: ['#FFFFFF', '#E8F4F3', '#C6E1DF', '#A3CCCA', '#73AA9A', '#4A7A7A'],
    shades: ['#254A49', '#1C3837', '#0F1F1E']
  },
  'Terracotta': {
    // Terracotta Clay — Anchor at 70%
    base: '#C97A5B',
    tints: ['#FFFFFF', '#FDF4F0', '#F9E3D6', '#F4D0B8', '#E8A885', '#D68B6A'],
    shades: ['#B86A4A', '#8B4F36', '#5C3424']
  },
  'Ochre': {
    // Sunlit Ochre — Anchor at 60%
    base: '#E8B86A',
    tints: ['#FFFFFF', '#FEF9F0', '#FDF0D6', '#FBE5B8', '#F5D085'],
    shades: ['#D4A55A', '#B8904A', '#7A5F31', '#3D2F18']
  },
  'Frosted': {
    // Frosted Sky — Anchor at 40%
    base: '#AFC6D9',
    tints: ['#FFFFFF', '#F8FBFF', '#E8F1F9', '#D6E4F0'],
    shades: ['#9BB3C7', '#87A0B5', '#738DA3', '#5F7A91', '#3D4B5C']
  },
  // Experimental Structural Gap Fillers
  'Burnt': {
    // Burnt Orange — Anchor at 70%
    base: '#D86B2D',
    tints: ['#FFFFFF', '#FEF4ED', '#FDE4D1', '#FBD1B0', '#F6A86E', '#E87A4A'],
    shades: ['#C55A22', '#9B4519', '#6B2F11']
  },
  'Olive': {
    // Olive Drab Green — Anchor at 70%
    base: '#6C7142',
    tints: ['#FFFFFF', '#F5F6F0', '#E8EBD8', '#D8DDB8', '#B8C088', '#9AA365'],
    shades: ['#5A5E37', '#484B2C', '#2F311D']
  },
  'Violet': {
    // Muted Violet Gray — Anchor at 60%
    base: '#756D8A',
    tints: ['#FFFFFF', '#F7F6F9', '#EDEBF2', '#E1DEE8', '#C8C1D1'],
    shades: ['#6A6278', '#5F5766', '#4A4350', '#2F2B33']
  },
  'Sand': {
    // Dusty Sand Beige — Anchor at 50%
    base: '#D8C6A8',
    tints: ['#FFFFFF', '#FDFCF9', '#FAF7F0', '#F5F0E4'],
    shades: ['#C4B296', '#B09E84', '#9C8A72', '#7A6B58', '#4D4336']
  }
};

const colorPresets = [
  { name: 'Current', value: 'currentColor' },
  { name: 'Sage (Anchor)', value: '#5A7D5E' },
  { name: 'Alpine (Anchor)', value: '#53675B' },
  { name: 'Charcoal (Anchor)', value: '#9CA3AF' },
  { name: 'Mustard (Anchor)', value: '#E3B505' },
  { name: 'Coral (Anchor)', value: '#F85E5E' },
  { name: 'Brown (Anchor)', value: '#584E47' },
  // Experimental Askew Complements
  { name: 'Ocean (Anchor)', value: '#2F5C5B' },
  { name: 'Terracotta (Anchor)', value: '#C97A5B' },
  { name: 'Ochre (Anchor)', value: '#E8B86A' },
  { name: 'Frosted (Anchor)', value: '#AFC6D9' },
  // Experimental Structural Gap Fillers
  { name: 'Burnt (Anchor)', value: '#D86B2D' },
  { name: 'Olive (Anchor)', value: '#6C7142' },
  { name: 'Violet (Anchor)', value: '#756D8A' },
  { name: 'Sand (Anchor)', value: '#D8C6A8' }
];

const sizePresets = [
  { name: 'XS', value: 12 },
  { name: 'SM', value: 16 },
  { name: 'MD', value: 24 },
  { name: 'LG', value: 32 },
  { name: 'XL', value: 48 }
];

export const IconCustomizer: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showCreateFromCode, setShowCreateFromCode] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [selectedColorFamily, setSelectedColorFamily] = useState<string>('Slate');
  const [customizerState, setCustomizerState] = useState<CustomizerState>({
    selectedIcon: null,
    selectedVariant: 'outline',
    size: 24,
    customSize: '24',
    color: 'currentColor',
    strokeWidth: 2,
    fillOpacity: 1,
    stepOpacities: [1.0, 0.8, 0.6, 0.4, 0.3, 0.2, 0.1],
    stepColors: ['#5A7D5E', '#53675B', '#E3B505', '#F85E5E', '#9CA3AF', '#584E47', '#2F5C5B'],
    customProps: {}
  });

  const categories = ['All', ...Array.from(new Set(iconDefinitions.map(icon => icon.category)))];

  const filteredIcons = iconDefinitions.filter(icon => {
    const matchesSearch = icon.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         icon.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || icon.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Debug: log the filtered icons count
  console.log('Filtered icons count:', filteredIcons.length, 'Total icons:', iconDefinitions.length);

  const handleIconSelect = (icon: IconDefinition) => {
    setCustomizerState(prev => ({
      ...prev,
      selectedIcon: icon,
      selectedVariant: icon.variants[0],
      customProps: {},
      // Initialize step colors for StairsStack
      stepColors: icon.name === 'StairsStack' ? ['#5A7D5E', '#53675B', '#E3B505', '#F85E5E', '#9CA3AF', '#584E47', '#2F5C5B'] : prev.stepColors
    }));
  };

  const handleVariantChange = (variant: IconVariant) => {
    setCustomizerState(prev => ({ ...prev, selectedVariant: variant }));
  };

  const handleCustomPropChange = (key: string, value: any) => {
    setCustomizerState(prev => ({
      ...prev,
      customProps: { ...prev.customProps, [key]: value }
    }));
  };

  const generateCode = () => {
    if (!customizerState.selectedIcon) return '';

    const { selectedIcon, selectedVariant, size, color, strokeWidth, fillOpacity, customProps } = customizerState;
    
    if (selectedIcon.name === 'PlateStack' || selectedIcon.name === 'StairsStack') {
      // Custom components with props
      const props = Object.entries(customProps)
        .filter(([_, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');
      
      return `<${selectedIcon.name}${props ? ` ${props}` : ''} />`;
    } else {
      // Standard Lucide icons
      const props = [
        `size={${size}}`,
        `color="${color}"`,
        `strokeWidth={${strokeWidth}}`
      ];
      
      if (selectedVariant === 'filled') {
        props.push(`fill="${color}"`);
        props.push(`fillOpacity={${fillOpacity}}`);
      }
      
      return `<${selectedIcon.name} ${props.join(' ')} />`;
    }
  };

  const copyToClipboard = async () => {
    const code = generateCode();
    try {
      await navigator.clipboard.writeText(code);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const saveToIconLibrary = () => {
    if (!customizerState.selectedIcon) return;
    
    const customIcon = {
      id: `custom-${customizerState.selectedIcon.name}-${Date.now()}`,
      name: `${customizerState.selectedIcon.name} (Custom)`,
      code: generateCode(),
      category: 'Custom',
      description: `Customized ${customizerState.selectedIcon.description}`,
      config: { ...customizerState }
    };
    
    // In a real app, you'd save this to your icon library
    console.log('Saving custom icon:', customIcon);
    alert('Custom icon saved! (Check console for details)');
  };

  const exportAsSVG = () => {
    if (!customizerState.selectedIcon) return;
    
    if (customizerState.selectedIcon.name === 'StairsStack') {
      const { customProps, stepColors, stepOpacities } = customizerState;
      const { variant = 'filled', size = 'xl', count = 4, showConnector = true } = customProps;
      
      // Calculate size in pixels
      const sizeMap = { sm: 16, md: 24, lg: 32, xl: 48 };
      const iconSize = sizeMap[size as keyof typeof sizeMap] || 48;
      
      // Generate SVG content
      const steps = Array.from({ length: count }).map((_, i) => {
        const x = 2 + (i * 2);
        const y = 16 - (i * 4);
        const stepColor = stepColors[i] || stepColors[0];
        const stepOpacity = variant === 'filled' ? (stepOpacities[i] || 1) : 1;
        
        return `<rect width="10" height="4" x="${x}" y="${y}" fill="${stepColor}" fill-opacity="${stepOpacity}" stroke="currentColor" stroke-width="2"/>`;
      }).join('\n    ');
      
      const connector = showConnector ? '<path d="M12 20h10V4h-4" stroke="currentColor" stroke-width="2"/>' : '';
      
      const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    ${steps}
    ${connector}
</svg>`;
      
      // Create and download SVG file
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stairs-stack-${variant}-${size}-${count}steps.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } else {
      // For standard icons, create a simple SVG
      const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <!-- Standard icon SVG would go here -->
</svg>`;
      
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${customizerState.selectedIcon.name.toLowerCase()}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleCustomSizeChange = (value: string) => {
    setCustomizerState(prev => ({ 
      ...prev, 
      customSize: value,
      size: parseInt(value) || 24
    }));
  };

  const handleStepOpacityChange = (index: number, value: number) => {
    const newOpacities = [...customizerState.stepOpacities];
    newOpacities[index] = value;
    setCustomizerState(prev => ({ ...prev, stepOpacities: newOpacities }));
  };

  const handleStepColorChange = (index: number, color: string) => {
    const newColors = [...customizerState.stepColors];
    newColors[index] = color;
    console.log('Step color changed:', { index, color, newColors });
    setCustomizerState(prev => ({ ...prev, stepColors: newColors }));
  };

  const createFromCode = () => {
    if (!customCode.trim()) return;
    
    // Create a custom icon definition from code
    const customIcon: IconDefinition = {
      name: 'Custom Icon',
      component: () => (
        <div 
          className="w-full h-full flex items-center justify-center"
          dangerouslySetInnerHTML={{ __html: customCode }}
        />
      ),
      category: 'Custom',
      description: 'Custom icon created from code',
      variants: ['custom']
    };
    
    setCustomizerState(prev => ({
      ...prev,
      selectedIcon: customIcon,
      selectedVariant: 'custom'
    }));
    setShowCreateFromCode(false);
  };

  const renderIconPreview = () => {
    if (!customizerState.selectedIcon) return null;

    const { selectedIcon, selectedVariant, size, color, strokeWidth, fillOpacity, customProps, stepOpacities, stepColors } = customizerState;
    const IconComponent = selectedIcon.component;

    if (selectedIcon.name === 'StairsStack') {
      const props = { 
        ...customProps,
        colors: stepColors,
        stepOpacities: customProps.variant === 'filled' ? stepOpacities : undefined
      };
      
      return (
        <div className="flex items-center justify-center p-8 bg-[var(--surface)]">
          <IconComponent {...props} />
        </div>
      );
    } else if (selectedIcon.name === 'Custom Icon') {
      return (
        <div className="flex items-center justify-center p-8 bg-[var(--surface)]">
          <div style={{ opacity: fillOpacity }}>
            <IconComponent />
          </div>
        </div>
      );
    } else {
      const iconProps: any = {
        size,
        color,
        strokeWidth
      };

      if (selectedVariant === 'filled') {
        iconProps.fill = color;
        iconProps.fillOpacity = fillOpacity;
      }

      return (
        <div className="flex items-center justify-center p-8 bg-[var(--surface)]">
          <IconComponent {...iconProps} />
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/elevation/studio" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700">
              <ArrowLeft className="h-4 w-4" />
              Back to Studio
            </Link>
            <span className="text-charcoal-400">•</span>
            <span className="text-sm text-charcoal-600">Studio Engine</span>
          </div>
          <h1 className="text-3xl font-bold text-charcoal-900 mb-2">Icon Customizer</h1>
          <p className="text-charcoal-600">Explore, customize, and get code for any icon in your design system</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Icon Browser */}
          <div className="lg:col-span-1">
            <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
              <h2 className="text-xl font-bold text-charcoal-900 mb-4">Icon Browser</h2>
              
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
                <input
                  type="text"
                  placeholder="Search icons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-transparent"
                />
              </div>

              {/* Category Filter */}
              <div className="mb-4">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-transparent"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Create from Code Button */}
              <div className="mb-4">
                <button
                  onClick={() => setShowCreateFromCode(!showCreateFromCode)}
                  className="w-full px-4 py-2 bg-[var(--mustard-dim)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors text-sm font-medium"
                >
                  {showCreateFromCode ? 'Cancel' : '+ Create from Code'}
                </button>
              </div>

              {/* Create from Code Input */}
              {showCreateFromCode && (
                <div className="mb-4 p-4 bg-[var(--surface)]">
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">
                    Paste your icon code (HTML/SVG)
                  </label>
                  <textarea
                    value={customCode}
                    onChange={(e) => setCustomCode(e.target.value)}
                    placeholder="<svg>...</svg> or <div>...</div>"
                    className="w-full h-20 px-3 py-2 border border-[var(--rule)] text-sm font-mono"
                  />
                  <button
                    onClick={createFromCode}
                    disabled={!customCode.trim()}
                    className="mt-2 px-4 py-1 bg-consulting-slate-blue-600 text-white rounded text-sm hover:bg-consulting-slate-blue-700 disabled:bg-[var(--surface)] disabled:cursor-not-allowed"
                  >
                    Create Icon
                  </button>
                </div>
              )}

              {/* Icon Grid */}
              <div className="grid grid-cols-4 gap-2 max-h-96 overflow-y-auto">
                {filteredIcons.length > 0 ? (
                  filteredIcons.map(icon => {
                    const IconComponent = icon.component;
                    const isSelected = customizerState.selectedIcon?.name === icon.name;
                    
                    return (
                      <button
                        key={icon.name}
                        onClick={() => handleIconSelect(icon)}
                        className={`p-3  border-2 transition-all flex items-center justify-center ${
                          isSelected 
                            ? 'border-consulting-slate-blue-500 bg-consulting-slate-blue-50' 
                            : 'border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
                        }`}
                        title={icon.description}
                      >
                        {icon.name === 'StairsStack' ? (
                          <StairsStack variant="filled" size="sm" count={4} showConnector={false} />
                        ) : (
                          <IconComponent size={20} />
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="col-span-4 text-center py-8 text-[var(--text-ter)]">
                    <p>No icons found</p>
                    <p className="text-sm">Try adjusting your search or category filter</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Customizer Panel */}
          <div className="lg:col-span-2">
            <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
              {customizerState.selectedIcon ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-charcoal-900">
                        {customizerState.selectedIcon.name}
                      </h2>
                      <p className="text-charcoal-600">
                        {customizerState.selectedIcon.description}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={copyToClipboard}
                        className="flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors"
                      >
                        <Copy size={16} />
                        Copy Code
                      </button>
                      <button
                        onClick={exportAsSVG}
                        className="flex items-center gap-2 px-4 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] transition-colors"
                      >
                        <Download size={16} />
                        Export SVG
                      </button>
                      <button
                        onClick={saveToIconLibrary}
                        className="flex items-center gap-2 px-4 py-2 bg-sage-600 text-white hover:bg-sage-700 transition-colors"
                      >
                        <Download size={16} />
                        Save to Library
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Preview */}
                    <div>
                      <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Preview</h3>
                      {renderIconPreview()}
                    </div>

                    {/* Controls */}
                    <div>
                      <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Customize</h3>
                      
                      {/* Variant Selection */}
                      {customizerState.selectedIcon.variants.length > 1 && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-charcoal-700 mb-2">
                            Variant
                          </label>
                          <div className="flex gap-2">
                            {customizerState.selectedIcon.variants.map(variant => (
                              <button
                                key={variant}
                                onClick={() => handleVariantChange(variant)}
                                className={`px-3 py-1  text-sm font-medium transition-colors ${
                                  customizerState.selectedVariant === variant
                                    ? 'bg-consulting-slate-blue-600 text-white'
                                    : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                                }`}
                              >
                                {variant}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Custom Props for PlateStack/StairsStack */}
                      {customizerState.selectedIcon.customProps && (
                        <div className="space-y-4 mb-4">
                          {Object.entries(customizerState.selectedIcon.customProps).map(([key, options]) => (
                            <div key={key}>
                              <label className="block text-sm font-medium text-charcoal-700 mb-2">
                                {key}
                              </label>
                              <select
                                value={customizerState.customProps[key] || ''}
                                onChange={(e) => handleCustomPropChange(key, e.target.value)}
                                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-transparent"
                              >
                                <option value="">Select {key}</option>
                                {Array.isArray(options) && options.map(option => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            </div>
                          ))}
                          
                          {/* Individual Step Controls for StairsStack */}
                          {customizerState.selectedIcon.name === 'StairsStack' && (
                            <div className="space-y-4">
                              {/* Individual Step Colors */}
                              <div>
                                <label className="block text-sm font-medium text-charcoal-700 mb-2">
                                  Step Colors
                                </label>
                                <div className="space-y-2">
                                  {customizerState.stepColors.map((stepColor, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                      <span className="text-sm text-[var(--text-sec)] w-8">Step {index + 1}</span>
                                      <div className="flex gap-1 flex-1">
                                        {Object.keys(colorRamps).map(family => (
                                          <button
                                            key={family}
                                            onClick={() => handleStepColorChange(index, colorRamps[family as keyof typeof colorRamps].base)}
                                            className={`w-6 h-6 rounded border-2 transition-all ${
                                              stepColor === colorRamps[family as keyof typeof colorRamps].base
                                                ? 'border-consulting-slate-blue-500 scale-110'
                                                : 'border-[var(--rule)] hover:border-[var(--rule)]'
                                            }`}
                                            style={{ backgroundColor: colorRamps[family as keyof typeof colorRamps].base }}
                                            title={`${family} Base`}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              
                              {/* Individual Step Opacities */}
                              {customizerState.customProps.variant === 'filled' && (
                                <div>
                                  <label className="block text-sm font-medium text-charcoal-700 mb-2">
                                    Step Opacities
                                  </label>
                                  <div className="space-y-2">
                                    {customizerState.stepOpacities.map((opacity, index) => (
                                      <div key={index} className="flex items-center gap-3">
                                        <span className="text-sm text-[var(--text-sec)] w-8">Step {index + 1}</span>
                                        <input
                                          type="range"
                                          min="0"
                                          max="1"
                                          step="0.1"
                                          value={opacity}
                                          onChange={(e) => handleStepOpacityChange(index, parseFloat(e.target.value))}
                                          className="flex-1"
                                        />
                                        <span className="text-sm text-[var(--text-sec)] w-8">{opacity.toFixed(1)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Custom Icon Controls */}
                      {customizerState.selectedIcon.name === 'Custom Icon' && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-charcoal-700 mb-2">
                            Opacity: {customizerState.fillOpacity}
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={customizerState.fillOpacity}
                            onChange={(e) => setCustomizerState(prev => ({ ...prev, fillOpacity: parseFloat(e.target.value) }))}
                            className="w-full"
                          />
                        </div>
                      )}

                      {/* Standard Icon Controls */}
                      {customizerState.selectedIcon.name !== 'StairsStack' && customizerState.selectedIcon.name !== 'Custom Icon' && (
                        <>
                          {/* Size */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-charcoal-700 mb-2">
                              Size
                            </label>
                            <div className="flex gap-2 mb-2">
                              {sizePresets.map(preset => (
                                <button
                                  key={preset.name}
                                  onClick={() => setCustomizerState(prev => ({ 
                                    ...prev, 
                                    size: preset.value,
                                    customSize: preset.value.toString()
                                  }))}
                                  className={`px-3 py-1  text-sm font-medium transition-colors ${
                                    customizerState.size === preset.value
                                      ? 'bg-consulting-slate-blue-600 text-white'
                                      : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                                  }`}
                                >
                                  {preset.name}
                                </button>
                              ))}
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={customizerState.customSize}
                                onChange={(e) => handleCustomSizeChange(e.target.value)}
                                className="w-20 px-2 py-1 border border-[var(--rule)] rounded text-sm"
                                placeholder="24"
                                min="8"
                                max="128"
                              />
                              <span className="text-sm text-[var(--text-sec)]">px</span>
                            </div>
                          </div>

                          {/* Color Family */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-charcoal-700 mb-2">
                              Color Family
                            </label>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {Object.keys(colorRamps).map(family => (
                                <button
                                  key={family}
                                  onClick={() => setSelectedColorFamily(family)}
                                  className={`px-3 py-1  text-sm font-medium transition-colors ${
                                    selectedColorFamily === family
                                      ? 'bg-consulting-slate-blue-600 text-white'
                                      : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                                  }`}
                                >
                                  {family}
                                </button>
                              ))}
                            </div>
                            
                            {/* Color Ramp */}
                            <div className="space-y-2">
                              <div>
                                <label className="block text-xs text-[var(--text-sec)] mb-1">Tints (Light to Base)</label>
                                <div className="flex gap-1">
                                  {colorRamps[selectedColorFamily as keyof typeof colorRamps].tints.map((color, index) => (
                                    <button
                                      key={`tint-${index}`}
                                      onClick={() => setCustomizerState(prev => ({ ...prev, color }))}
                                      className={`w-8 h-8 rounded border-2 transition-all ${
                                        customizerState.color === color
                                          ? 'border-consulting-slate-blue-500 scale-110'
                                          : 'border-[var(--rule)] hover:border-[var(--rule)]'
                                      }`}
                                      style={{ backgroundColor: color }}
                                      title={`${selectedColorFamily} Tint ${index + 1}`}
                                    />
                                  ))}
                                </div>
                              </div>
                              
                              <div>
                                <label className="block text-xs text-[var(--text-sec)] mb-1">Base</label>
                                <div className="flex justify-start">
                                  <button
                                    onClick={() => setCustomizerState(prev => ({ ...prev, color: colorRamps[selectedColorFamily as keyof typeof colorRamps].base }))}
                                    className={`w-12 h-8 rounded border-2 transition-all ${
                                      customizerState.color === colorRamps[selectedColorFamily as keyof typeof colorRamps].base
                                        ? 'border-consulting-slate-blue-500 scale-110'
                                        : 'border-[var(--rule)] hover:border-[var(--rule)]'
                                    }`}
                                    style={{ backgroundColor: colorRamps[selectedColorFamily as keyof typeof colorRamps].base }}
                                    title={`${selectedColorFamily} Base`}
                                  />
                                </div>
                              </div>
                              
                              <div>
                                <label className="block text-xs text-[var(--text-sec)] mb-1">Shades (Base to Dark)</label>
                                <div className="flex gap-1">
                                  {colorRamps[selectedColorFamily as keyof typeof colorRamps].shades.map((color, index) => (
                                    <button
                                      key={`shade-${index}`}
                                      onClick={() => setCustomizerState(prev => ({ ...prev, color }))}
                                      className={`w-8 h-8 rounded border-2 transition-all ${
                                        customizerState.color === color
                                          ? 'border-consulting-slate-blue-500 scale-110'
                                          : 'border-[var(--rule)] hover:border-[var(--rule)]'
                                      }`}
                                      style={{ backgroundColor: color }}
                                      title={`${selectedColorFamily} Shade ${index + 1}`}
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            {/* Current Color */}
                            <div className="mt-3 p-2 bg-[var(--surface)] rounded text-xs">
                              <span className="text-[var(--text-sec)]">Selected: </span>
                              <span className="font-mono">{customizerState.color}</span>
                            </div>
                          </div>

                          {/* Stroke Width */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-charcoal-700 mb-2">
                              Stroke Width: {customizerState.strokeWidth}
                            </label>
                            <input
                              type="range"
                              min="1"
                              max="4"
                              step="0.5"
                              value={customizerState.strokeWidth}
                              onChange={(e) => setCustomizerState(prev => ({ ...prev, strokeWidth: parseFloat(e.target.value) }))}
                              className="w-full"
                            />
                          </div>

                          {/* Fill Opacity (for filled variant) */}
                          {customizerState.selectedVariant === 'filled' && (
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-charcoal-700 mb-2">
                                Fill Opacity: {customizerState.fillOpacity}
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                value={customizerState.fillOpacity}
                                onChange={(e) => setCustomizerState(prev => ({ ...prev, fillOpacity: parseFloat(e.target.value) }))}
                                className="w-full"
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Generated Code */}
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Generated Code</h3>
                    <div className="bg-[var(--bg)] text-[var(--text-pri)] p-4 font-mono text-sm overflow-x-auto">
                      <pre>{generateCode()}</pre>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Eye className="mx-auto h-12 w-12 text-[var(--text-ter)] mb-4" />
                  <h3 className="text-lg font-medium text-charcoal-900 mb-2">Select an Icon</h3>
                  <p className="text-charcoal-600">Choose an icon from the browser to start customizing</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
